import React, { useState, useEffect } from 'react';
import { Play, Clock, Eye, Search, Grid, List, Loader2, RefreshCw, ExternalLink, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getChannelVideos, getChannelShorts, getChannelInfo } from '../services/youtubeBackendService.js';

function AllVideos() {
    const [hoveredVideo, setHoveredVideo]         = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('सभी');
    const [viewMode, setViewMode]                 = useState('grid');
    const [searchTerm, setSearchTerm]             = useState('');
    const [videos, setVideos]                     = useState([]);
    const [shorts, setShorts]                     = useState([]);
    const [loading, setLoading]                   = useState(true);
    const [error, setError]                       = useState(null);
    const [activeSection, setActiveSection]       = useState('videos');
    const [channelInfo, setChannelInfo]           = useState({
        videosUrl: 'https://www.youtube.com/@IndiaJuncationnews/videos',
        shortsUrl: 'https://www.youtube.com/@IndiaJuncationnews/shorts',
    });

    const navigate = useNavigate();

    useEffect(() => { loadAll(); }, []);

    const loadAll = async (forceRefresh = false) => {
        try {
            setLoading(true);
            setError(null);

            const [info, fetchedVideos, fetchedShorts] = await Promise.all([
                getChannelInfo(),
                getChannelVideos(50, !forceRefresh),
                getChannelShorts(!forceRefresh),
            ]);

            if (info) setChannelInfo(info);

            if (fetchedVideos && fetchedVideos.length > 0) {
                setVideos(fetchedVideos);
                console.log(`✅ Loaded ${fetchedVideos.length} videos`);
            } else {
                setError('वीडियो लोड नहीं हो सके। कृपया बाद में पुनः प्रयास करें।');
            }

            if (fetchedShorts && fetchedShorts.length > 0) {
                setShorts(fetchedShorts);
                console.log(`✅ Loaded ${fetchedShorts.length} shorts`);
            }

        } catch (err) {
            console.error('Error loading:', err);
            setError('वीडियो लोड करने में समस्या आई। कृपया बाद में प्रयास करें।');
        } finally {
            setLoading(false);
        }
    };

    const currentVideos  = activeSection === 'shorts' ? shorts : videos;
    const categories     = ['सभी', ...new Set(currentVideos.map(v => v.category).filter(Boolean))];
    const filteredVideos = currentVideos.filter(video => {
        const matchesCategory = selectedCategory === 'सभी' || video.category === selectedCategory;
        const matchesSearch   = video.title.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const handleVideoClick = (video) => navigate('/videoplay', { state: { video } });

    // ── Shorts Card (portrait 9:16) ──
    const ShortCard = ({ video }) => (
        <div
            className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group relative bg-black"
            style={{ aspectRatio: '9/16', maxHeight: '380px' }}
            onClick={() => handleVideoClick(video)}
        >
            <img
                src={video.thumbnail} alt={video.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90"
                loading="lazy"
                onError={(e) => { e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/20" />

            <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                <Zap className="w-3 h-3" /> Shorts
            </div>

            {video.duration && (
                <div className="absolute top-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded font-semibold">
                    {video.duration}
                </div>
            )}

            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="bg-white/90 rounded-full p-3 transform scale-90 group-hover:scale-100 transition-transform">
                    <Play className="w-6 h-6 text-red-600 fill-red-600" />
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white text-xs sm:text-sm font-bold line-clamp-2 group-hover:text-yellow-300 transition-colors mb-1">
                    {video.title}
                </h3>
                <div className="flex items-center gap-2 text-gray-300 text-xs">
                    {video.views   && <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{video.views}</span>}
                    {video.timeAgo && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{video.timeAgo}</span>}
                </div>
            </div>
        </div>
    );

    // ── Regular Video Card ──
    const VideoCard = ({ video, isListView = false }) => (
        <div
            className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group ${isListView ? 'flex' : 'h-full'}`}
            onMouseEnter={() => setHoveredVideo(video.id)}
            onMouseLeave={() => setHoveredVideo(null)}
            onClick={() => handleVideoClick(video)}
        >
            <div className={`relative overflow-hidden ${isListView ? 'w-48 sm:w-64 flex-shrink-0' : ''}`}>
                <img
                    src={video.thumbnail} alt={video.title}
                    className={`w-full object-cover group-hover:scale-110 transition-transform duration-500 ${isListView ? 'h-full' : 'h-40 sm:h-48'}`}
                    loading="lazy"
                    onError={(e) => { e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`; }}
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 rounded-full p-3 sm:p-4 transform scale-90 group-hover:scale-100 transition-transform">
                        <Play className="w-6 h-6 sm:w-8 sm:h-8 text-red-600 fill-red-600" />
                    </div>
                </div>
                {video.duration && (
                    <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded font-semibold">{video.duration}</div>
                )}
                {video.category && (
                    <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded font-bold uppercase">{video.category}</div>
                )}
                <div className="absolute top-2 right-2 bg-white rounded-full p-1 sm:p-1.5">
                    <Play className="w-3 h-3 sm:w-4 sm:h-4 text-red-600 fill-red-600" />
                </div>
            </div>
            <div className={`${isListView ? 'p-3 sm:p-4 flex-1' : 'p-3 sm:p-4'}`}>
                <h3 className={`font-bold group-hover:text-red-600 transition-colors ${isListView ? 'text-sm sm:text-base line-clamp-3' : 'text-sm sm:text-base line-clamp-2'}`}>
                    {video.title}
                </h3>
                <div className="flex items-center gap-2 sm:gap-3 text-gray-600 text-xs sm:text-sm mt-2 sm:mt-3">
                    {video.views && (
                        <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            <span className="hidden sm:inline">{video.views}</span>
                            <span className="sm:hidden">{video.views.split(' ')[0]}</span>
                        </span>
                    )}
                    {video.timeAgo && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{video.timeAgo}</span>}
                </div>
            </div>
        </div>
    );

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900 px-4">
            <div className="text-center">
                <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-red-600 animate-spin mx-auto mb-4 sm:mb-6" />
                <h2 className="text-white text-xl sm:text-2xl font-bold mb-2">सभी वीडियो लोड हो रहे हैं...</h2>
                <p className="text-gray-400 text-sm sm:text-base">India Juncation News से वीडियो fetch कर रहे हैं</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4">
            <div className="text-center max-w-md">
                <div className="bg-red-600/20 border-2 border-red-600 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                    <h2 className="text-red-400 text-xl sm:text-2xl font-bold mb-2 sm:mb-3">{error}</h2>
                    <p className="text-gray-300 text-sm sm:text-base mb-4">कृपया फिर से प्रयास करें।</p>
                </div>
                <div className="space-y-3">
                    <button onClick={() => loadAll(true)}
                        className="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-all font-semibold">
                        <RefreshCw className="w-5 h-5" />फिर से लोड करें
                    </button>
                    <a href={channelInfo.videosUrl} target="_blank" rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-2 bg-white/10 text-white px-6 py-3 rounded-lg hover:bg-white/20 transition-all font-semibold">
                        <ExternalLink className="w-5 h-5" />YouTube चैनल पर जाएं
                    </a>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">

            {/* ── Sticky Header ── */}
            <div className="bg-gradient-to-r from-gray-900 to-black shadow-lg sticky top-0 z-40 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">

                    {/* Top row */}
                    <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
                        <h1 className="text-white text-lg sm:text-2xl md:text-3xl font-bold flex items-center gap-2 sm:gap-3">
                            <div className="bg-gradient-to-br from-red-600 to-red-700 p-1.5 sm:p-2 rounded-lg shadow-lg">
                                {activeSection === 'shorts'
                                    ? <Zap className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                                    : <Play className="w-4 h-4 sm:w-6 sm:h-6 text-white" />}
                            </div>
                            <span className="whitespace-nowrap">{activeSection === 'shorts' ? 'Shorts' : 'सभी वीडियो'}</span>
                            <span className="text-xs sm:text-sm font-normal text-gray-400 hidden sm:inline">({filteredVideos.length})</span>
                        </h1>

                        <div className="flex items-center gap-2 sm:gap-3">
                            <button onClick={() => loadAll(true)}
                                className="p-1.5 sm:p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
                                <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </button>
                            {activeSection === 'videos' && (
                                <div className="flex bg-white/10 rounded-lg p-0.5 sm:p-1">
                                    <button onClick={() => setViewMode('grid')}
                                        className={`p-1.5 sm:p-2 rounded ${viewMode === 'grid' ? 'bg-white text-black' : 'text-white hover:bg-white/10'} transition-all`}>
                                        <Grid className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                    <button onClick={() => setViewMode('list')}
                                        className={`p-1.5 sm:p-2 rounded ${viewMode === 'list' ? 'bg-white text-black' : 'text-white hover:bg-white/10'} transition-all`}>
                                        <List className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                </div>
                            )}
                            <a href={activeSection === 'shorts' ? channelInfo.shortsUrl : channelInfo.videosUrl}
                                target="_blank" rel="noopener noreferrer"
                                className="hidden sm:flex items-center gap-2 bg-red-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-red-700 transition-all font-semibold text-sm">
                                <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />YouTube
                            </a>
                        </div>
                    </div>

                    {/* ── Videos / Shorts Toggle ── */}
                    <div className="flex gap-2 mb-3 sm:mb-4">
                        <button
                            onClick={() => { setActiveSection('videos'); setSelectedCategory('सभी'); }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-all ${
                                activeSection === 'videos' ? 'bg-red-600 text-white shadow-lg scale-105' : 'bg-white/10 text-white hover:bg-white/20'
                            }`}>
                            <Play className="w-4 h-4" />Videos
                            <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs">{videos.length}</span>
                        </button>
                        <button
                            onClick={() => { setActiveSection('shorts'); setSelectedCategory('सभी'); }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-all ${
                                activeSection === 'shorts' ? 'bg-yellow-400 text-black shadow-lg scale-105' : 'bg-white/10 text-white hover:bg-white/20'
                            }`}>
                            <Zap className="w-4 h-4" />Shorts
                            <span className="bg-black/20 px-1.5 py-0.5 rounded-full text-xs">{shorts.length}</span>
                        </button>
                    </div>

                    {/* Search */}
                    <div className="relative mb-3 sm:mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                        <input type="text" placeholder="वीडियो खोजें..." value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/50 transition-all text-sm sm:text-base"
                        />
                    </div>

                    {/* Category filter */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                        {categories.map((category) => (
                            <button key={category} onClick={() => setSelectedCategory(category)}
                                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-semibold whitespace-nowrap transition-all text-xs sm:text-sm ${
                                    selectedCategory === category ? 'bg-yellow-400 text-black shadow-lg scale-105' : 'bg-white/20 text-white hover:bg-white/30'
                                }`}>
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Main Content ── */}
            <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
                {filteredVideos.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                        <div className="bg-white/10 rounded-lg p-6 sm:p-8 max-w-md mx-auto">
                            <Search className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-white text-lg sm:text-xl font-bold mb-2">कोई वीडियो नहीं मिला</p>
                            <p className="text-gray-400 text-sm sm:text-base">
                                {searchTerm ? 'अलग शब्दों से खोजने की कोशिश करें' : 'इस श्रेणी में कोई वीडियो नहीं है'}
                            </p>
                            {(searchTerm || selectedCategory !== 'सभी') && (
                                <button onClick={() => { setSearchTerm(''); setSelectedCategory('सभी'); }}
                                    className="mt-4 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-all text-sm sm:text-base">
                                    सभी वीडियो देखें
                                </button>
                            )}
                        </div>
                    </div>
                ) : activeSection === 'shorts' ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                        {filteredVideos.map((video) => <ShortCard key={video.id} video={video} />)}
                    </div>
                ) : (
                    <div className={
                        viewMode === 'grid'
                            ? 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6'
                            : 'space-y-3 sm:space-y-4'
                    }>
                        {filteredVideos.map((video) => <VideoCard key={video.id} video={video} isListView={viewMode === 'list'} />)}
                    </div>
                )}
            </main>

            {filteredVideos.length > 0 && (
                <div className="max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8 text-center space-y-3">
                    <p className="text-gray-400 text-sm sm:text-base">{filteredVideos.length} वीडियो दिखाए जा रहे हैं</p>
                    <a href={activeSection === 'shorts' ? channelInfo.shortsUrl : channelInfo.videosUrl}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-white text-black px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-bold hover:bg-yellow-400 transition-all shadow-lg hover:scale-105 text-sm sm:text-base">
                        <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                        YouTube पर और {activeSection === 'shorts' ? 'Shorts' : 'वीडियो'} देखें
                    </a>
                </div>
            )}

            <style jsx>{`
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div>
    );
}

export default AllVideos;